import React, { useEffect, useMemo, useState } from 'react';
import { logAdminAction } from '../../lib/adminAuth';
import { getSessionHeaders } from '../../lib/serverSession';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '';

interface UserOption { id: number; username: string | null; email: string | null; full_name: string | null }
interface PhaseOption { id: number; phase_number: number; phase_name: string }
interface WinnerRecord { id: number; user_id: number; phase_id: number; rank: number; prize_amount: number; prize_tier: number | null; competition_id: string | null; awarded_at: string; email_sent: boolean | null }
interface CompetitionRecord {
  id: string;
  name?: string | null;
  phase_id: number;
  status: string;
  is_active?: boolean;
  start_date?: string | null;
  end_date?: string | null;
  total_prize_pool: number | null;
}
interface TierConfigRecord { id: string; competition_id: string; tier_level: number; tier_name: string; emoji: string; volume_min: number; volume_max: number | null; min_direct_buyers: number; pool_allocation_percent: number; display_order: number }
interface LeaderboardEntry { user_id: number; username: string; total_volume: number; total_referrals: number; rank: number; prize_percent: number; prize_bonus?: number; prize_amount: number }
interface Props { currentUser?: any }

type StatusMsg = { type: 'success' | 'error' | 'info'; text: string } | null;

const formatOrdinal = (n: number) => {
  const v = n % 100;
  if (v >= 11 && v <= 13) return `${n}th`;
  switch (n % 10) {
    case 1: return `${n}st`;
    case 2: return `${n}nd`;
    case 3: return `${n}rd`;
    default: return `${n}th`;
  }
};

const getRankEmoji = (rank: number) => {
  if (rank === 1) return '🥇';
  if (rank === 2) return '🥈';
  if (rank === 3) return '🥉';
  if (rank <= 10) return '🏆';
  if (rank <= 20) return '🏅';
  if (rank <= 30) return '🎖️';
  return '⭐';
};

const __getRankBand = (rank: number) => {
  if (rank <= 3) return rank === 1 ? 'bg-yellow-900/40 border-yellow-500/50' : rank === 2 ? 'bg-gray-700/40 border-gray-400/50' : 'bg-orange-900/40 border-orange-500/50';
  if (rank <= 10) return 'bg-blue-900/20 border-blue-600/30';
  if (rank <= 20) return 'bg-indigo-900/15 border-indigo-600/20';
  if (rank <= 30) return 'bg-purple-900/10 border-purple-600/15';
  return 'bg-gray-800/10 border-gray-600/10';
};

const CompetitionWinnersManager: React.FC<Props> = ({ currentUser }) => {
  const [users, setUsers] = useState<UserOption[]>([]);
  const [phases, setPhases] = useState<PhaseOption[]>([]);
  const [winners, setWinners] = useState<WinnerRecord[]>([]);
  const [competitions, setCompetitions] = useState<CompetitionRecord[]>([]);
  const [_tierConfigs, setTierConfigs] = useState<TierConfigRecord[]>([]);

  // Top 50 leaderboard state
  const [leaderboard, setLeaderboard] = useState<LeaderboardEntry[]>([]);
  const [selectedEntries, setSelectedEntries] = useState<Set<number>>(new Set());
  const [lbPhaseId, setLbPhaseId] = useState<number | ''>('');
  const [lbLoading, setLbLoading] = useState(false);
  const [lbPrizePool, setLbPrizePool] = useState(0);
  const [lbRevenue, setLbRevenue] = useState(0);
  const [lbSharesSold, setLbSharesSold] = useState(0);
  const [lbCompetitionId, setLbCompetitionId] = useState<string | null>(null);
  const [awardedMap, setAwardedMap] = useState<Record<number, any>>({});
  const [_lbIsTop50, setLbIsTop50] = useState(true);
  const [lbMaxPositions, setLbMaxPositions] = useState(50);
  const [lbPoolPercent, setLbPoolPercent] = useState(15);
  const [lbWeekLabel, setLbWeekLabel] = useState<string | null>(null);
  const [lbAwardWeek, setLbAwardWeek] = useState<number | null>(null);
  const [lbScoringClock, setLbScoringClock] = useState<string | null>(null);
  const [lbCompetitionName, setLbCompetitionName] = useState<string | null>(null);

  // Manual award (legacy / special prizes)
  const [manualMode, setManualMode] = useState(false);
  const [userSearch, setUserSearch] = useState('');
  const [selectedUserId, setSelectedUserId] = useState<number | ''>('');
  const [selectedPhaseId, setSelectedPhaseId] = useState<number | ''>('');
  const [position, setPosition] = useState<string>('');
  const [prizeAmount, setPrizeAmount] = useState<string>('');

  const [filterPhaseId, setFilterPhaseId] = useState<number | ''>('');
  const [filterUser, setFilterUser] = useState('');
  const [_loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [status, setStatus] = useState<StatusMsg>(null);

  useEffect(() => { loadInitialData(); }, []);

  const loadInitialData = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/admin/competition-winners`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getSessionHeaders() },
        body: JSON.stringify({ operation: 'load_initial_data', data: {} })
      });
      const result = await response.json();
      if (!response.ok) throw new Error(result.error);

      setUsers(result.users || []);
      setPhases(result.phases || []);
      setWinners(result.winners || []);
      setCompetitions(result.competitions || []);
      setTierConfigs(result.tierConfigs || []);
      setStatus(null);
    } catch (error: any) {
      console.error('[CompetitionWinnersManager] Error loading data:', error);
      if (error?.message?.includes('does not exist')) {
        setStatus({ type: 'error', text: 'The competition_winners table does not exist yet. Please run the SQL migration to create it before using this tool.' });
      } else {
        setStatus({ type: 'error', text: 'Failed to load data. Please try again.' });
      }
    } finally {
      setLoading(false);
    }
  };

  // Same Club scorer as the user Gold Diggers dashboard (submit-time for Phase 9+)
  const fetchLeaderboard = async (phaseId: number, competitionId?: string | null) => {
    setLbLoading(true);
    setLeaderboard([]);
    setSelectedEntries(new Set());
    try {
      const response = await fetch(`${API_BASE_URL}/api/admin/competition-winners`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getSessionHeaders() },
        body: JSON.stringify({
          operation: 'fetch_leaderboard',
          data: { phaseId, competitionId: competitionId || undefined },
        })
      });
      const result = await response.json();
      if (!response.ok) throw new Error(result.error);
      setLeaderboard(result.leaderboard || []);
      setLbPrizePool(result.totalPrizePool || 0);
      setLbRevenue(result.totalRevenue || 0);
      setLbSharesSold(result.totalSharesSold || 0);
      setLbCompetitionId(result.competitionId || competitionId || null);
      setLbCompetitionName(result.competition?.name || null);
      setLbScoringClock(result.scoringClock || null);
      setAwardedMap(result.awardedMap || {});
      setLbIsTop50(result.isTop50 ?? true);
      setLbMaxPositions(result.maxPositions || (result.isTop50 ? 50 : 10));
      setLbPoolPercent(result.config?.prize_pool_percent ?? (result.weeklyEnabled ? 10 : 15));
      setLbWeekLabel(result.weeklyEnabled
        ? `Week ${result.week}`
        : 'Monthly phase window');
      setLbAwardWeek(result.weeklyEnabled && Number.isInteger(Number(result.week)) && Number(result.week) > 0
        ? Number(result.week)
        : null);
      // Auto-select all un-awarded entries
      const unawarded = new Set<number>();
      (result.leaderboard || []).forEach((e: LeaderboardEntry) => {
        if (!result.awardedMap?.[e.user_id]) unawarded.add(e.user_id);
      });
      setSelectedEntries(unawarded);
    } catch (error: any) {
      setStatus({ type: 'error', text: `Failed to fetch leaderboard: ${error.message}` });
    } finally {
      setLbLoading(false);
    }
  };

  const usersById = useMemo(() => {
    const map: Record<number, UserOption> = {};
    users.forEach(u => { map[u.id] = u; });
    return map;
  }, [users]);

  const phasesById = useMemo(() => {
    const map: Record<number, PhaseOption> = {};
    phases.forEach(p => { map[p.id] = p; });
    return map;
  }, [phases]);

  const filteredUserOptions = useMemo(() => {
    const term = userSearch.trim().toLowerCase();
    if (!term) return users;
    return users.filter(u => {
      const idMatch = String(u.id) === term;
      const usernameMatch = u.username?.toLowerCase().includes(term);
      const emailMatch = u.email?.toLowerCase().includes(term);
      const nameMatch = u.full_name?.toLowerCase().includes(term);
      return idMatch || usernameMatch || emailMatch || nameMatch;
    });
  }, [users, userSearch]);

  const filteredWinners = useMemo(() => {
    return winners.filter(w => {
      if (filterPhaseId && w.phase_id !== filterPhaseId) return false;
      if (!filterUser.trim()) return true;
      const user = usersById[w.user_id];
      const search = filterUser.toLowerCase();
      const username = user?.username?.toLowerCase() || '';
      const email = user?.email?.toLowerCase() || '';
      const fullName = user?.full_name?.toLowerCase() || '';
      return username.includes(search) || email.includes(search) || fullName.includes(search) || String(w.user_id) === search;
    }).sort((a, b) => a.rank - b.rank); // Sort by rank ascending: 1st place at top
  }, [winners, filterPhaseId, filterUser, usersById]);

  const resetForm = () => {
    setSelectedUserId('');
    setSelectedPhaseId('');
    setPosition('');
    setPrizeAmount('');
    setStatus(null);
  };

  // Toggle selection for batch award
  const toggleEntry = (userId: number) => {
    setSelectedEntries(prev => {
      const next = new Set(prev);
      if (next.has(userId)) next.delete(userId);
      else next.add(userId);
      return next;
    });
  };

  // Award a single winner from the leaderboard
  const [awardingUserId, setAwardingUserId] = useState<number | null>(null);
  const handleSingleAward = async (entry: LeaderboardEntry) => {
    if (!window.confirm(
      `Award ${formatOrdinal(entry.rank)} place prize to ${entry.username}?\n\n$${entry.prize_amount.toFixed(2)} USDT will be credited to their commission balance.\n\nContinue?`
    )) return;

    setAwardingUserId(entry.user_id);
    setStatus({ type: 'info', text: `Awarding ${entry.username}...` });
    try {
      const response = await fetch(`${API_BASE_URL}/api/admin/competition-winners`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getSessionHeaders() },
        body: JSON.stringify({
          operation: 'batch_award',
          data: {
            winners: [{
              user_id: entry.user_id,
              username: entry.username,
              rank: entry.rank,
              prize_amount: entry.prize_amount,
              total_volume: entry.total_volume,
              total_referrals: entry.total_referrals
            }],
            phaseId: lbPhaseId,
            competitionId: lbCompetitionId,
            awardWeek: lbAwardWeek,
            awardedByAdminId: currentUser?.adminUser?.id || null
          }
        })
      });
      const result = await response.json();
      if (!response.ok) throw new Error(result.error);

      // Check if award failed (duplicate or other backend error)
      if (result.errors?.length > 0 && (!result.awarded || result.awarded.length === 0)) {
        const errMsg = result.errors[0]?.error || 'Unknown error';
        setStatus({ type: 'error', text: `⚠️ Failed to award ${entry.username}: ${errMsg}` });
        setAwardingUserId(null);
        return;
      }

      // Send email
      const winner = result.awarded?.[0];
      if (winner) {
        const user = usersById[entry.user_id];
        const emailTo = user?.email?.trim();
        if (emailTo) {
          const phase = phasesById[typeof lbPhaseId === 'number' ? lbPhaseId : 0];
          const phaseName = phase?.phase_name || 'Phase';
          const winnerName = user?.full_name || user?.username || '';
          const ord = formatOrdinal(winner.rank);
          const placeDesc = `${ord} place`;
          const subject = `🏆 Congratulations on your ${placeDesc} in the Aureus Alliance Competition!`;
          const dashboardUrl = 'https://aureus.africa/dashboard';
          const html = `<div style="font-family:Arial,sans-serif;line-height:1.6;color:#111;max-width:640px;margin:0 auto;">
<h2 style="color:#b8860b;">Congratulations ${winnerName}! 🏆</h2>
<p>You have officially won <strong>${placeDesc}</strong> in the <strong>${phaseName} Gold Diggers Club Competition</strong>.</p>
<p>We have credited <strong>$${winner.prize_amount.toFixed(2)} USDT</strong> to your Aureus Alliance commission balance.</p>
<div style="background:#f9f5ec;border:1px solid #d4a843;border-radius:8px;padding:16px;margin:24px 0;">
<h3 style="color:#b8860b;margin:0 0 12px 0;font-size:15px;">📊 ${phaseName} Competition Highlights</h3>
<table style="width:100%;border-collapse:collapse;font-size:14px;">
<tr><td style="padding:6px 0;color:#555;">Total Shares Sold</td><td style="padding:6px 0;text-align:right;font-weight:bold;color:#111;">${lbSharesSold.toLocaleString()} shares</td></tr>
<tr style="border-top:1px solid #e8dfc8;"><td style="padding:6px 0;color:#555;">Phase Revenue</td><td style="padding:6px 0;text-align:right;font-weight:bold;color:#111;">$${lbRevenue.toLocaleString()} USDT</td></tr>
<tr style="border-top:1px solid #e8dfc8;"><td style="padding:6px 0;color:#555;">Total Prize Pool (Top ${lbMaxPositions}, ${lbPoolPercent}%)</td><td style="padding:6px 0;text-align:right;font-weight:bold;color:#b8860b;">$${lbPrizePool.toLocaleString()} USDT</td></tr>
<tr style="border-top:1px solid #e8dfc8;"><td style="padding:6px 0;color:#555;">Your Prize (${entry.prize_percent}%${entry.prize_bonus > 0 ? ` + $${entry.prize_bonus} equal share` : ''})</td><td style="padding:6px 0;text-align:right;font-weight:bold;color:#16a34a;">$${winner.prize_amount.toFixed(2)} USDT</td></tr>
</table>
</div>
<p>This prize gives you a powerful <strong>head start in building a long-term retirement fund</strong> through share accumulation.</p>
<p>For maximum impact, we strongly encourage you to <strong>use these winnings to purchase additional Aureus Africa shares</strong>. More shares mean a larger share of future gold-backed dividend income for you and your family.</p>
<p style="text-align:center;margin:32px 0;"><a href="${dashboardUrl}" style="background:#b8860b;color:white;padding:12px 24px;border-radius:6px;text-decoration:none;font-weight:bold;">View Your Winnings &amp; Buy More Shares</a></p>
<p>Warm regards,<br/>The Aureus Alliance Team</p>
</div>`;
          try {
            const emailResp = await fetch(`${API_BASE_URL}/api/send-mass-email`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', ...getSessionHeaders() },
              body: JSON.stringify({ recipients: [emailTo], subject, htmlContent: html, textContent: html.replace(/<[^>]*>/g, '').replace(/\s+/g, ' ').trim(), isTest: false })
            });
            const emailResult = await emailResp.json();
            if (emailResult.success || emailResp.ok) {
              const msgId = emailResult.results?.[0]?.messageIds?.[0] || emailResult.results?.[0]?.messageId || null;
              await fetch(`${API_BASE_URL}/api/admin/competition-winners`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', ...getSessionHeaders() },
                body: JSON.stringify({ operation: 'update_email_sent', data: { winnerId: winner.id, emailSent: true, emailMessageId: msgId } })
              });
            }
          } catch (emailErr) { console.error('Email error:', emailErr); }
        }
      }

      await logAdminAction(currentUser?.email || currentUser?.adminUser?.email || 'unknown', 'competition_single_award', 'competition_winner', String(entry.user_id), { rank: entry.rank, amount: entry.prize_amount, username: entry.username });
      setStatus({ type: 'success', text: `✅ Awarded $${entry.prize_amount.toFixed(2)} to ${entry.username} (${formatOrdinal(entry.rank)} place). Email sent.` });

      if (typeof lbPhaseId === 'number') await fetchLeaderboard(lbPhaseId, lbCompetitionId);
      await loadInitialData();
    } catch (error: any) {
      setStatus({ type: 'error', text: `Failed to award ${entry.username}: ${error?.message || 'Unknown error'}` });
    } finally {
      setAwardingUserId(null);
    }
  };

  const toggleAll = () => {
    const unawardedIds = leaderboard.filter(e => !awardedMap[e.user_id]).map(e => e.user_id);
    if (selectedEntries.size >= unawardedIds.length) setSelectedEntries(new Set());
    else setSelectedEntries(new Set(unawardedIds));
  };

  // Batch award handler
  const handleBatchAward = async () => {
    const selected = leaderboard.filter(e => selectedEntries.has(e.user_id) && !awardedMap[e.user_id]);
    if (selected.length === 0) { setStatus({ type: 'error', text: 'No un-awarded winners selected.' }); return; }

    const totalAmount = selected.reduce((sum, e) => sum + e.prize_amount, 0);
    if (!window.confirm(
      `Award prizes to ${selected.length} winners sequentially?\n\nTotal: $${totalAmount.toFixed(2)} USDT\n\nEach winner will be awarded one at a time with commission credit + congratulatory email.\n\nContinue?`
    )) return;

    setSubmitting(true);
    let successCount = 0;
    let errorCount = 0;
    let successAmount = 0;
    const failedUsers: string[] = [];

    for (let i = 0; i < selected.length; i++) {
      const entry = selected[i];
      setStatus({ type: 'info', text: `⏳ Awarding ${i + 1} of ${selected.length}: ${entry.username} (${formatOrdinal(entry.rank)} place, $${entry.prize_amount.toFixed(0)})...` });
      setAwardingUserId(entry.user_id);

      try {
        // 1. Award via backend (single winner)
        const response = await fetch(`${API_BASE_URL}/api/admin/competition-winners`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', ...getSessionHeaders() },
          body: JSON.stringify({
            operation: 'batch_award',
            data: {
              winners: [{
                user_id: entry.user_id,
                username: entry.username,
                rank: entry.rank,
                prize_amount: entry.prize_amount,
                total_volume: entry.total_volume,
                total_referrals: entry.total_referrals
              }],
              phaseId: lbPhaseId,
              competitionId: lbCompetitionId,
              awardWeek: lbAwardWeek,
              awardedByAdminId: currentUser?.adminUser?.id || null
            }
          })
        });
        const result = await response.json();
        if (!response.ok) throw new Error(result.error);

        if (result.errors?.length > 0 && (!result.awarded || result.awarded.length === 0)) {
          throw new Error(result.errors[0]?.error || 'Award failed');
        }

        const winner = result.awarded?.[0];
        if (!winner) throw new Error('No award record returned');

        // 2. Send email
        const user = usersById[entry.user_id];
        const emailTo = user?.email?.trim();
        if (emailTo) {
          const phase = phasesById[typeof lbPhaseId === 'number' ? lbPhaseId : 0];
          const phaseName = phase?.phase_name || 'Phase';
          const winnerName = user?.full_name || user?.username || '';
          const ord = formatOrdinal(winner.rank);
          const placeDesc = `${ord} place`;
          const subject = `🏆 Congratulations on your ${placeDesc} in the Aureus Alliance Competition!`;
          const dashboardUrl = 'https://aureus.africa/dashboard';
          const prizePercent = entry.prize_bonus > 0
            ? `${entry.prize_percent}% + $${entry.prize_bonus} equal share`
            : `${entry.prize_percent || ''}%`;
          const html = `<div style="font-family:Arial,sans-serif;line-height:1.6;color:#111;max-width:640px;margin:0 auto;">
<h2 style="color:#b8860b;">Congratulations ${winnerName}! 🏆</h2>
<p>You have officially won <strong>${placeDesc}</strong> in the <strong>${phaseName} Gold Diggers Club Competition</strong>.</p>
<p>We have credited <strong>$${winner.prize_amount.toFixed(2)} USDT</strong> to your Aureus Alliance commission balance.</p>
<div style="background:#f9f5ec;border:1px solid #d4a843;border-radius:8px;padding:16px;margin:24px 0;">
<h3 style="color:#b8860b;margin:0 0 12px 0;font-size:15px;">📊 ${phaseName} Competition Highlights</h3>
<table style="width:100%;border-collapse:collapse;font-size:14px;">
<tr><td style="padding:6px 0;color:#555;">Total Shares Sold</td><td style="padding:6px 0;text-align:right;font-weight:bold;color:#111;">${lbSharesSold.toLocaleString()} shares</td></tr>
<tr style="border-top:1px solid #e8dfc8;"><td style="padding:6px 0;color:#555;">Phase Revenue</td><td style="padding:6px 0;text-align:right;font-weight:bold;color:#111;">$${lbRevenue.toLocaleString()} USDT</td></tr>
<tr style="border-top:1px solid #e8dfc8;"><td style="padding:6px 0;color:#555;">Total Prize Pool (Top ${lbMaxPositions}, ${lbPoolPercent}%)</td><td style="padding:6px 0;text-align:right;font-weight:bold;color:#b8860b;">$${lbPrizePool.toLocaleString()} USDT</td></tr>
<tr style="border-top:1px solid #e8dfc8;"><td style="padding:6px 0;color:#555;">Your Prize (${prizePercent})</td><td style="padding:6px 0;text-align:right;font-weight:bold;color:#16a34a;">$${winner.prize_amount.toFixed(2)} USDT</td></tr>
</table>
</div>
<p>This prize gives you a powerful <strong>head start in building a long-term retirement fund</strong> through share accumulation.</p>
<p>For maximum impact, we strongly encourage you to <strong>use these winnings to purchase additional Aureus Africa shares</strong>. More shares mean a larger share of future gold-backed dividend income for you and your family.</p>
<p style="text-align:center;margin:32px 0;"><a href="${dashboardUrl}" style="background:#b8860b;color:white;padding:12px 24px;border-radius:6px;text-decoration:none;font-weight:bold;">View Your Winnings &amp; Buy More Shares</a></p>
<p>Warm regards,<br/>The Aureus Alliance Team</p>
</div>`;
          try {
            const emailResp = await fetch(`${API_BASE_URL}/api/send-mass-email`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', ...getSessionHeaders() },
              body: JSON.stringify({ recipients: [emailTo], subject, htmlContent: html, textContent: html.replace(/<[^>]*>/g, '').replace(/\s+/g, ' ').trim(), isTest: false })
            });
            const emailResult = await emailResp.json();
            if (emailResult.success || emailResp.ok) {
              const msgId = emailResult.results?.[0]?.messageIds?.[0] || emailResult.results?.[0]?.messageId || null;
              await fetch(`${API_BASE_URL}/api/admin/competition-winners`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', ...getSessionHeaders() },
                body: JSON.stringify({ operation: 'update_email_sent', data: { winnerId: winner.id, emailSent: true, emailMessageId: msgId } })
              });
            }
          } catch (emailErr) { console.error('Email error for', entry.username, emailErr); }
        }

        successCount++;
        successAmount += entry.prize_amount;
      } catch (err: any) {
        errorCount++;
        failedUsers.push(`${entry.username}: ${err?.message || 'Unknown error'}`);
        console.error(`Award failed for ${entry.username}:`, err);
      }
    }

    setAwardingUserId(null);
    await logAdminAction(currentUser?.email || currentUser?.adminUser?.email || 'unknown', 'competition_batch_award', 'competition_winner', String(lbPhaseId), { count: successCount, totalAmount: successAmount, errors: errorCount });

    if (errorCount === 0) {
      setStatus({ type: 'success', text: `✅ All ${successCount} winners awarded ($${successAmount.toFixed(0)} USDT). Emails sent.` });
    } else {
      setStatus({ type: 'error', text: `⚠️ ${successCount} awarded, ${errorCount} failed. Failed: ${failedUsers.join('; ')}` });
    }

    // Refresh
    if (typeof lbPhaseId === 'number') await fetchLeaderboard(lbPhaseId, lbCompetitionId);
    await loadInitialData();
    setSubmitting(false);
  };

  // Manual single-award handler (for special prizes or legacy phases)
  const handleAwardPrize = async () => {
    setStatus(null);
    const userId = typeof selectedUserId === 'number' ? selectedUserId : parseInt(String(selectedUserId));
    const phaseId = typeof selectedPhaseId === 'number' ? selectedPhaseId : parseInt(String(selectedPhaseId));
    const amount = parseFloat(prizeAmount);
    if (!userId || !phaseId || position.trim() === '' || prizeAmount.trim() === '') {
      setStatus({ type: 'error', text: 'Please select a user, phase, position and prize amount.' });
      return;
    }

    const pos = parseInt(position, 10);
    const phase = phasesById[phaseId];

    if (isNaN(pos) || pos < 0) {
      setStatus({ type: 'error', text: 'Position / rank must be 0 or higher.' });
      return;
    }
    if (isNaN(amount) || amount <= 0) {
      setStatus({ type: 'error', text: 'Prize amount must be a positive number.' });
      return;
    }
    const user = usersById[userId];
    const ordinal = formatOrdinal(pos);
    const placeDescription = pos === 0 ? 'special competition prize' : `${ordinal} place`;
    const summary = `${user?.username || 'User ' + userId} – ${placeDescription} in ${phase?.phase_name || `Phase ${phase?.phase_number || ''}`}`;
    if (!window.confirm(`Are you sure you want to award ${amount.toFixed(2)} USDT to ${summary}?\n\nThis will credit their USDT balance and record a competition prize transaction.`)) {
      return;
    }
    setSubmitting(true);
    try {
      // Award prize via API
      const response = await fetch(`${API_BASE_URL}/api/admin/competition-winners`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getSessionHeaders() },
        body: JSON.stringify({
          operation: 'award_prize',
          data: {
            userId,
            phaseId,
            rank: pos,
            prizeAmount: amount,
            username: user?.username || '',
            awardedByAdminId: currentUser?.adminUser?.id || null
          }
        })
      });
      const result = await response.json();
      if (!response.ok) {
        setStatus({ type: 'error', text: result.error || 'Failed to award prize.' });
        return;
      }
      const winnerRow = result.data;

      // Send congratulatory email via server-side API (best effort)
      const emailTo = user?.email?.trim();
      if (emailTo) {
        const subject = `🏆 Congratulations on your ${placeDescription} in the Aureus Alliance Competition!`;
        const dashboardUrl = 'https://aureus.africa/dashboard';
        const phaseName = phase?.phase_name || `Phase ${phase?.phase_number || ''}`;
        const winnerName = user.full_name || user.username || '';
        // Include stats if leaderboard was loaded for this phase; otherwise omit the section
        const hasStats = typeof lbPhaseId === 'number' && lbPhaseId === phaseId && lbRevenue > 0;
        const statsSection = hasStats ? `
            <div style="background:#f9f5ec;border:1px solid #d4a843;border-radius:8px;padding:16px;margin:24px 0;">
              <h3 style="color:#b8860b;margin:0 0 12px 0;font-size:15px;">📊 ${phaseName} Competition Highlights</h3>
              <table style="width:100%;border-collapse:collapse;font-size:14px;">
                <tr><td style="padding:6px 0;color:#555;">Total Shares Sold</td><td style="padding:6px 0;text-align:right;font-weight:bold;color:#111;">${lbSharesSold.toLocaleString()} shares</td></tr>
                <tr style="border-top:1px solid #e8dfc8;"><td style="padding:6px 0;color:#555;">Phase Revenue</td><td style="padding:6px 0;text-align:right;font-weight:bold;color:#111;">$${lbRevenue.toLocaleString()} USDT</td></tr>
                <tr style="border-top:1px solid #e8dfc8;"><td style="padding:6px 0;color:#555;">Total Prize Pool (Top ${lbMaxPositions}, ${lbPoolPercent}%)</td><td style="padding:6px 0;text-align:right;font-weight:bold;color:#b8860b;">$${lbPrizePool.toLocaleString()} USDT</td></tr>
                <tr style="border-top:1px solid #e8dfc8;"><td style="padding:6px 0;color:#555;">Your Prize</td><td style="padding:6px 0;text-align:right;font-weight:bold;color:#16a34a;">$${amount.toFixed(2)} USDT</td></tr>
              </table>
            </div>` : '';
        const html = `
          <div style="font-family: Arial, sans-serif; line-height: 1.6; color: #111; max-width: 640px; margin: 0 auto;">
            <h2 style="color: #b8860b;">Congratulations ${winnerName}! 🏆</h2>
            <p>You have officially won a <strong>${placeDescription}</strong> in the <strong>${phaseName} Gold Diggers Club Competition</strong>.</p>
            <p>We have credited <strong>$${amount.toFixed(2)} USDT</strong> to your Aureus Alliance commission balance.</p>
            ${statsSection}
            <p>This prize gives you a powerful <strong>head start in building a long-term retirement fund</strong> through share accumulation.</p>
            <p>For maximum impact, we strongly encourage you to <strong>use these winnings to purchase additional Aureus Africa shares</strong>. More shares mean a larger share of future gold-backed dividend income for you and your family.</p>
            <p>Every extra block of shares you accumulate today can help create <strong>stronger financial security for both you and your children</strong> in the years ahead.</p>
            <p style="text-align:center; margin: 32px 0;">
              <a href="${dashboardUrl}" style="background:#b8860b; color:white; padding:12px 24px; border-radius:6px; text-decoration:none; font-weight:bold;">View Your Winnings &amp; Buy More Shares</a>
            </p>
            <p>If you have any questions, reply to this email or contact support from inside your dashboard.</p>
            <p>Warm regards,<br/>The Aureus Alliance Team</p>
          </div>`;
        try {
          // Use server-side API endpoint (same as Mass Email Broadcast)
          const emailResp = await fetch(`${API_BASE_URL}/api/send-mass-email`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', ...getSessionHeaders() },
            body: JSON.stringify({
              recipients: [emailTo],
              subject,
              htmlContent: html,
              textContent: html.replace(/<[^>]*>/g, '').replace(/\s+/g, ' ').trim(),
              isTest: false
            })
          });

          const emailResult = await emailResp.json();

          // Check for success - mass email returns success:true and results array with messageIds (plural)
          if (emailResult.success || emailResp.ok) {
            const messageId = emailResult.results?.[0]?.messageIds?.[0] || emailResult.results?.[0]?.messageId || null;
            // Update email sent status via API
            await fetch(`${API_BASE_URL}/api/admin/competition-winners`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', ...getSessionHeaders() },
              body: JSON.stringify({
                operation: 'update_email_sent',
                data: { winnerId: winnerRow.id, emailSent: true, emailMessageId: messageId }
              })
            });
          } else {
            console.error('[CompetitionWinnersManager] Email send failed:', emailResult.error || 'Unknown error');
          }
        } catch (emailError) {
          console.error('[CompetitionWinnersManager] Failed to send prize email:', emailError);
        }
      }
      await logAdminAction(
        currentUser?.email || currentUser?.adminUser?.email || 'unknown',
        'competition_prize_award',
        'competition_winner',
        String(userId),
        { userId, phaseId, position: pos, amount }
      );
      setStatus({ type: 'success', text: `Successfully awarded ${amount.toFixed(2)} USDT to ${summary}.` });
      setWinners(prev => [winnerRow as WinnerRecord, ...prev]);
      resetForm();
    } catch (error: any) {
      console.error('[CompetitionWinnersManager] Error awarding prize:', error);
      setStatus({ type: 'error', text: error?.message || 'Failed to award prize. Please try again.' });
    } finally {
      setSubmitting(false);
    }
  };

  const handleResendEmail = async (winner: WinnerRecord) => {
    const user = usersById[winner.user_id];
    const phase = phasesById[winner.phase_id];
    const emailTo = user?.email?.trim();
    if (!emailTo) {
      alert('No email address on file for this user.');
      return;
    }
    const ordinal = formatOrdinal(winner.rank);
    const placeDescription = winner.rank === 0 ? 'special competition prize' : `${ordinal} place`;
    const subject = `Your Aureus Alliance competition prize for ${placeDescription}`;
    const dashboardUrl = 'https://aureus.africa/dashboard';
    const html = `
      <div style="font-family: Arial, sans-serif; line-height: 1.6; color: #111; max-width: 640px; margin: 0 auto;">
        <h2 style="color: #b8860b;">Your Competition Prize Is Waiting</h2>
        <p>This is a friendly reminder that you earned a <strong>${placeDescription}</strong> in the <strong>${phase?.phase_name || `Phase ${phase?.phase_number || ''}`} competition</strong>.</p>
        <p>We previously credited <strong>${winner.prize_amount.toFixed(2)} USDT</strong> to your Aureus Alliance commission balance.</p>
        <p>To get the maximum long-term benefit, consider using these funds to <strong>purchase additional Aureus Africa shares</strong> so you can grow your future dividend income.</p>
        <p>Those extra shares can help build a stronger <strong>retirement base and long-term financial security</strong> for you and your children.</p>
        <p style="text-align:center; margin: 32px 0;">
          <a href="${dashboardUrl}" style="background:#b8860b; color:white; padding:12px 24px; border-radius:6px; text-decoration:none; font-weight:bold;">Log In &amp; Grow Your Shareholding</a>
        </p>
        <p>If you have any questions, reply to this email or contact support from inside your dashboard.</p>
        <p>Warm regards,<br/>The Aureus Alliance Team</p>
      </div>`;
    try {
      // Use server-side API endpoint (consistent with handleAwardPrize)
      const emailResp = await fetch(`${API_BASE_URL}/api/send-mass-email`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getSessionHeaders() },
        body: JSON.stringify({
          recipients: [emailTo],
          subject,
          htmlContent: html,
          textContent: html.replace(/<[^>]*>/g, '').replace(/\s+/g, ' ').trim(),
          isTest: false
        })
      });
      const emailResult = await emailResp.json();

      if (emailResult.success || emailResp.ok) {
        const messageId = emailResult.results?.[0]?.messageIds?.[0] || emailResult.results?.[0]?.messageId || null;
        // Update email sent status via API
        await fetch(`${API_BASE_URL}/api/admin/competition-winners`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', ...getSessionHeaders() },
          body: JSON.stringify({
            operation: 'update_email_sent',
            data: { winnerId: winner.id, emailSent: true, emailMessageId: messageId }
          })
        });
        setStatus({ type: 'success', text: 'Prize email resent successfully.' });
      } else {
        setStatus({ type: 'error', text: emailResult.error || 'Failed to resend email.' });
      }
    } catch (error: any) {
      console.error('[CompetitionWinnersManager] Error resending email:', error);
      setStatus({ type: 'error', text: error?.message || 'Failed to resend email.' });
    }
  };

  // Summary stats for batch
  const selectedTotal = leaderboard.filter(e => selectedEntries.has(e.user_id) && !awardedMap[e.user_id]).reduce((s, e) => s + e.prize_amount, 0);
  const selectedCount = leaderboard.filter(e => selectedEntries.has(e.user_id) && !awardedMap[e.user_id]).length;
  const awardedCount = Object.keys(awardedMap).length;

  return (
    <div className="flex flex-col gap-6">
      {/* Header */}
      <div>
        <h2 className="text-xl font-bold mb-1">🏆 Competition Winners & Prize Distribution</h2>
        <p className="text-gray-400 text-sm max-w-3xl">
          Select the same Club competition shown on the user Gold Diggers dashboard. Sales Volume is scored identically (payment submit time for Phase 9 onward, not approval). Review prize amounts, then award. Each winner receives a USDT commission credit and congratulatory email.
        </p>
      </div>

      {/* Status */}
      {status && (
        <div className={`p-3 rounded-lg text-sm ${status.type === 'success' ? 'bg-green-900/20 border border-green-600/30 text-green-300' : status.type === 'error' ? 'bg-red-900/20 border border-red-600/30 text-red-300' : 'bg-blue-900/20 border border-blue-600/30 text-blue-300'}`}>
          {status.text}
        </div>
      )}

      {/* Club competition selector — same windows as user Gold Diggers Club */}
      <div className="border border-gray-700 rounded-xl p-5 bg-gray-900/80">
        <div className="flex items-end gap-4 flex-wrap">
          <div className="flex-1 min-w-[280px]">
            <label className="block text-sm font-medium text-gray-300 mb-1">Club competition</label>
            <select
              value={lbCompetitionId || ''}
              onChange={e => {
                const id = e.target.value || null;
                setLbCompetitionId(id);
                const c = competitions.find(x => x.id === id);
                setLbPhaseId(c ? c.phase_id : '');
                setLbCompetitionName(c?.name || null);
              }}
              className="w-full bg-gray-800 border border-gray-600 rounded-lg px-3 py-2.5 text-white">
              <option value="">Choose the same Club board as users...</option>
              {competitions.filter(c => c.status !== 'cancelled').map(c => {
                const phase = phasesById[c.phase_id];
                const label = c.name || (phase ? `Phase ${phase.phase_number} – ${phase.phase_name}` : `Phase ${c.phase_id}`);
                return (
                  <option key={c.id} value={c.id}>{label} ({c.status})</option>
                );
              })}
            </select>
          </div>
          <button onClick={() => typeof lbPhaseId === 'number' && fetchLeaderboard(lbPhaseId, lbCompetitionId)}
            disabled={!lbPhaseId || lbLoading}
            className={`px-5 py-2.5 rounded-lg font-semibold text-sm transition ${!lbPhaseId || lbLoading ? 'bg-gray-700 text-gray-400 cursor-not-allowed' : 'bg-blue-600 hover:bg-blue-500 text-white cursor-pointer'}`}>
            {lbLoading ? '⏳ Loading...' : '📊 Load Leaderboard'}
          </button>
          <button onClick={() => setManualMode(!manualMode)}
            className="px-4 py-2.5 rounded-lg font-medium text-sm border border-gray-600 text-gray-300 hover:bg-gray-800 transition cursor-pointer">
            {manualMode ? '✕ Close Manual' : '✏️ Manual Award'}
          </button>
        </div>
      </div>
      {(lbCompetitionName || lbScoringClock) && leaderboard.length > 0 && (
        <div className="text-xs text-gray-400 -mt-2">
          {lbCompetitionName ? <span className="text-white font-medium">{lbCompetitionName}</span> : null}
          {lbWeekLabel ? <span>{lbCompetitionName ? ' · ' : ''}{lbWeekLabel}</span> : null}
          {lbScoringClock ? (
            <span>
              {(lbCompetitionName || lbWeekLabel) ? ' · ' : ''}
              scored by {lbScoringClock === 'submit' ? 'payment submit time (same as user Club)' : 'approval / created_at (Phase 8 and earlier)'}
            </span>
          ) : null}
        </div>
      )}

      {/* Summary Cards */}
      {leaderboard.length > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div className="bg-gray-800/60 border border-gray-700 rounded-xl p-4 text-center">
            <div className="text-2xl font-bold text-white">{leaderboard.length}</div>
            <div className="text-xs text-gray-400 mt-1">Ranked Competitors</div>
          </div>
          <div className="bg-gray-800/60 border border-yellow-700/40 rounded-xl p-4 text-center">
            <div className="text-2xl font-bold text-yellow-400">${lbPrizePool.toLocaleString()}</div>
            <div className="text-xs text-gray-400 mt-1">Prize Pool ({lbPoolPercent}% of {lbWeekLabel || 'window'} revenue)</div>
          </div>
          <div className="bg-gray-800/60 border border-green-700/40 rounded-xl p-4 text-center">
            <div className="text-2xl font-bold text-green-400">${lbRevenue.toLocaleString()}</div>
            <div className="text-xs text-gray-400 mt-1">Club qualifying L1 volume</div>
          </div>
          <div className="bg-gray-800/60 border border-blue-700/40 rounded-xl p-4 text-center">
            <div className="text-2xl font-bold text-blue-400">{awardedCount} / {leaderboard.length}</div>
            <div className="text-xs text-gray-400 mt-1">Already Awarded (Top {lbMaxPositions})</div>
          </div>
        </div>
      )}

      {/* Leaderboard Table with Batch Award */}
      {leaderboard.length > 0 && (
        <div className="border border-gray-700 rounded-xl bg-gray-900/80 overflow-hidden">
          <div className="flex items-center justify-between p-4 border-b border-gray-700 bg-gray-800/50">
            <div className="flex items-center gap-3">
              <label className="flex items-center gap-2 cursor-pointer text-sm text-gray-300">
                <input type="checkbox" checked={selectedCount > 0 && selectedCount === leaderboard.filter(e => !awardedMap[e.user_id]).length}
                  onChange={toggleAll} className="w-4 h-4 rounded accent-yellow-500" />
                Select All ({leaderboard.filter(e => !awardedMap[e.user_id]).length} un-awarded)
              </label>
              {selectedCount > 0 && (
                <span className="text-sm text-yellow-400 font-medium">{selectedCount} selected · ${selectedTotal.toFixed(2)} USDT</span>
              )}
            </div>
            <button onClick={handleBatchAward} disabled={submitting || selectedCount === 0}
              className={`px-5 py-2 rounded-lg font-bold text-sm transition ${submitting || selectedCount === 0 ? 'bg-gray-700 text-gray-500 cursor-not-allowed' : 'bg-green-600 hover:bg-green-500 text-white cursor-pointer shadow-lg shadow-green-900/30'}`}>
              {submitting ? '⏳ Awarding...' : `🏆 Award ${selectedCount} Winners ($${selectedTotal.toFixed(2)})`}
            </button>
          </div>
          <div className="max-h-[520px] overflow-y-auto">
            <table className="w-full text-sm">
              <thead className="sticky top-0 bg-gray-800 z-10">
                <tr>
                  <th className="p-3 text-left w-10"></th>
                  <th className="p-3 text-left">Rank</th>
                  <th className="p-3 text-left">Username</th>
                  <th className="p-3 text-right">L1 Sales Volume (Club)</th>
                  <th className="p-3 text-center">Referrals</th>
                  <th className="p-3 text-center">Prize %</th>
                  <th className="p-3 text-right">Prize Amount</th>
                  <th className="p-3 text-center">Status</th>
                  <th className="p-3 text-center">Action</th>
                </tr>
              </thead>
              <tbody>
                {leaderboard.map(entry => {
                  const isAwarded = !!awardedMap[entry.user_id];
                  const isSelected = selectedEntries.has(entry.user_id);
                  const isAwarding = awardingUserId === entry.user_id;
                  return (
                    <tr key={entry.user_id}
                      className={`border-t border-gray-800 transition ${isAwarded ? 'opacity-50 bg-green-900/5' : isSelected ? 'bg-yellow-900/10' : 'hover:bg-gray-800/50'}`}>
                      <td className="p-3">
                        {!isAwarded && (
                          <input type="checkbox" checked={isSelected} onChange={() => toggleEntry(entry.user_id)} className="w-4 h-4 rounded accent-yellow-500 cursor-pointer" />
                        )}
                      </td>
                      <td className="p-3 font-bold">
                        <span className="mr-1">{getRankEmoji(entry.rank)}</span>{formatOrdinal(entry.rank)}
                      </td>
                      <td className="p-3">
                        <span className="font-medium text-white">{entry.username}</span>
                        <span className="text-gray-500 text-xs ml-2">ID:{entry.user_id}</span>
                      </td>
                      <td className="p-3 text-right font-mono text-gray-300">${entry.total_volume.toLocaleString()}</td>
                      <td className="p-3 text-center text-gray-400">{entry.total_referrals}</td>
                      <td className="p-3 text-center text-yellow-400 font-medium">{entry.prize_percent}%{entry.prize_bonus > 0 ? ` + $${entry.prize_bonus}` : ''}</td>
                      <td className="p-3 text-right font-bold text-green-400">${entry.prize_amount.toFixed(2)}</td>
                      <td className="p-3 text-center">
                        {isAwarded
                          ? <span className="px-2 py-1 rounded-full text-xs bg-green-900/30 text-green-400 border border-green-700/40">✓ Awarded</span>
                          : <span className="px-2 py-1 rounded-full text-xs bg-gray-800 text-gray-400 border border-gray-700">Pending</span>}
                      </td>
                      <td className="p-3 text-center">
                        {isAwarded ? (
                          <span className="text-gray-600 text-xs">—</span>
                        ) : (
                          <button
                            onClick={() => handleSingleAward(entry)}
                            disabled={isAwarding || submitting}
                            className={`px-3 py-1 rounded-lg text-xs font-semibold transition ${isAwarding ? 'bg-yellow-800 text-yellow-200 cursor-wait' : 'bg-green-700 hover:bg-green-600 text-white cursor-pointer'}`}
                          >
                            {isAwarding ? '⏳...' : '💰 Award'}
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Manual Award */}
      {manualMode && (
        <div className="border border-gray-700 rounded-xl p-5 bg-gray-900/80">
          <h3 className="text-base font-semibold mb-4">✏️ Manual Prize Award <span className="text-xs text-gray-500 font-normal ml-2">For special prizes or legacy phases</span></h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div>
              <label className="block text-xs text-gray-400 mb-1">Search User</label>
              <input value={userSearch} onChange={e => setUserSearch(e.target.value)} placeholder="Username, email, or ID"
                className="w-full bg-gray-800 border border-gray-600 rounded-lg px-3 py-2 text-white text-sm" />
            </div>
            <div>
              <label className="block text-xs text-gray-400 mb-1">Select Winner</label>
              <select value={selectedUserId} onChange={e => setSelectedUserId(e.target.value ? parseInt(e.target.value) : '')}
                className="w-full bg-gray-800 border border-gray-600 rounded-lg px-3 py-2 text-white text-sm">
                <option value="">Select user...</option>
                {filteredUserOptions.map(u => (<option key={u.id} value={u.id}>{u.username || `User ${u.id}`} ({u.email || 'no email'})</option>))}
              </select>
            </div>
            <div>
              <label className="block text-xs text-gray-400 mb-1">Phase</label>
              <select value={selectedPhaseId} onChange={e => setSelectedPhaseId(e.target.value ? parseInt(e.target.value) : '')}
                className="w-full bg-gray-800 border border-gray-600 rounded-lg px-3 py-2 text-white text-sm">
                <option value="">Select phase...</option>
                {phases.map(p => (<option key={p.id} value={p.id}>Phase {p.phase_number}</option>))}
              </select>
            </div>
            <div className="flex gap-2">
              <div className="flex-1">
                <label className="block text-xs text-gray-400 mb-1">Rank</label>
                <input type="number" min={0} value={position} onChange={e => setPosition(e.target.value)} placeholder="1"
                  className="w-full bg-gray-800 border border-gray-600 rounded-lg px-3 py-2 text-white text-sm" />
              </div>
              <div className="flex-1">
                <label className="block text-xs text-gray-400 mb-1">Amount ($)</label>
                <input type="number" min={0} step="0.01" value={prizeAmount} onChange={e => setPrizeAmount(e.target.value)} placeholder="2500"
                  className="w-full bg-gray-800 border border-gray-600 rounded-lg px-3 py-2 text-white text-sm" />
              </div>
            </div>
          </div>
          <div className="flex gap-3 mt-4">
            <button onClick={handleAwardPrize} disabled={submitting}
              className={`px-5 py-2 rounded-lg font-semibold text-sm ${submitting ? 'bg-gray-700 text-gray-400' : 'bg-green-600 hover:bg-green-500 text-white'} cursor-pointer`}>
              {submitting ? 'Processing...' : 'Award Prize'}
            </button>
            <button onClick={resetForm} className="px-4 py-2 rounded-lg border border-gray-600 text-gray-300 text-sm hover:bg-gray-800 cursor-pointer">Reset</button>
          </div>
        </div>
      )}

      {/* Winners History */}
      <div className="border border-gray-700 rounded-xl bg-gray-900/80 overflow-hidden">
        <div className="p-4 border-b border-gray-700 flex items-center gap-3">
          <h3 className="text-base font-semibold flex-shrink-0">📋 Award History</h3>
          <select value={filterPhaseId} onChange={e => setFilterPhaseId(e.target.value ? parseInt(e.target.value) : '')}
            className="bg-gray-800 border border-gray-600 rounded-lg px-3 py-1.5 text-white text-sm">
            <option value="">All phases</option>
            {phases.map(p => (<option key={p.id} value={p.id}>Phase {p.phase_number}</option>))}
          </select>
          <input value={filterUser} onChange={e => setFilterUser(e.target.value)} placeholder="Filter by user..."
            className="flex-1 bg-gray-800 border border-gray-600 rounded-lg px-3 py-1.5 text-white text-sm" />
        </div>
        <div className="max-h-[360px] overflow-y-auto">
          <table className="w-full text-xs">
            <thead className="sticky top-0 bg-gray-800 z-10">
              <tr>
                <th className="p-2.5 text-left">Winner</th>
                <th className="p-2.5 text-left">Phase</th>
                <th className="p-2.5 text-center">Rank</th>
                <th className="p-2.5 text-right">Prize (USDT)</th>
                <th className="p-2.5 text-left">Awarded</th>
                <th className="p-2.5 text-center">Email</th>
                <th className="p-2.5 text-center">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredWinners.length === 0 ? (
                <tr><td colSpan={7} className="p-6 text-center text-gray-500">No winners recorded yet.</td></tr>
              ) : (
                filteredWinners.map(w => {
                  const user = usersById[w.user_id];
                  const phase = phasesById[w.phase_id];
                  return (
                    <tr key={w.id} className="border-t border-gray-800 hover:bg-gray-800/30">
                      <td className="p-2.5">
                        <span className="font-medium">{user?.username || `User ${w.user_id}`}</span>
                        <span className="text-gray-500 ml-1.5">{user?.email || ''}</span>
                      </td>
                      <td className="p-2.5 text-gray-300">{phase ? `Phase ${phase.phase_number}` : `#${w.phase_id}`}</td>
                      <td className="p-2.5 text-center font-medium">{getRankEmoji(w.rank)} {formatOrdinal(w.rank)}</td>
                      <td className="p-2.5 text-right font-mono text-green-400">${w.prize_amount.toFixed(2)}</td>
                      <td className="p-2.5 text-gray-400">{new Date(w.awarded_at).toLocaleDateString()}</td>
                      <td className="p-2.5 text-center">
                        {w.email_sent ? <span className="text-green-400">✓</span> : <span className="text-red-400">✕</span>}
                      </td>
                      <td className="p-2.5 text-center">
                        <button onClick={() => handleResendEmail(w)}
                          className="px-2 py-1 rounded text-xs border border-gray-600 text-blue-400 hover:bg-gray-800 cursor-pointer">Resend</button>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default CompetitionWinnersManager;

